import React from "react";
import Switch from "@mui/material/Switch";
import { Dispatcher } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import {
  applyDefaultHighSensitivityIfUnset,
  shouldApplyAutopatrolHighSensitivityDefault,
} from "./autopatrolDefaultHighSensitivity";
import { trioActiveProductCount } from "./trioMutualExclusionRecoveryMode";

const disabledChecker = (
  labelName: string,
  selectedGroups: any,
  allowConcurrentLineCrossingIntruderProducts = false
) => {
  const intruderMotionConflict =
    selectedGroups.intruder.selected && selectedGroups.motionPlus.selected;
  const trioCount = trioActiveProductCount(selectedGroups);

  if (
    labelName === "intruder" ||
    labelName === "vehicle" ||
    labelName === "bike"
  ) {
    if (allowConcurrentLineCrossingIntruderProducts) {
      if (intruderMotionConflict) {
        return selectedGroups.intruder.disabled;
      }
      return (
        selectedGroups.intruder.disabled || selectedGroups.motionPlus.selected
      );
    }

    if (trioCount >= 2 && selectedGroups.intruder.selected) {
      return selectedGroups.intruder.disabled;
    }
    if (trioCount === 2 && !selectedGroups.intruder.selected) {
      return true;
    }
    return (
      selectedGroups.intruder.disabled ||
      selectedGroups.motionPlus.selected ||
      selectedGroups.lineCrossing.selected
    );
  } else if (
    labelName === "intruder_plus" ||
    labelName === "vehicle_plus" ||
    labelName === "bike_plus"
  ) {
    return selectedGroups.intruderPlus.disabled;
  } else if (
    labelName === "intruder_motion" ||
    labelName === "vehicle_motion" ||
    labelName === "bike_motion"
  ) {
    if (allowConcurrentLineCrossingIntruderProducts) {
      if (intruderMotionConflict) {
        return false;
      }
      return selectedGroups.intruder.selected;
    }

    if (trioCount >= 2 && selectedGroups.motionPlus.selected) {
      return false;
    }
    if (trioCount === 2 && !selectedGroups.motionPlus.selected) {
      return true;
    }
    return (
      selectedGroups.intruder.selected || selectedGroups.lineCrossing.selected
    );
  } else if (
    labelName === "intruder_line_crossing" ||
    labelName === "vehicle_line_crossing"
  ) {
    if (allowConcurrentLineCrossingIntruderProducts) {
      return false;
    }

    if (trioCount >= 2 && selectedGroups.lineCrossing.selected) {
      return false;
    }
    if (trioCount === 2 && !selectedGroups.lineCrossing.selected) {
      return true;
    }
    return (
      selectedGroups.intruder.selected || selectedGroups.motionPlus.selected
    );
  } else return false;
};

export const switchCreator = (
  object: any,
  selectedGroups: any,
  groupLabelName: string,
  setSelectedGroups: any,
  setLocalProductsData: any,
  setProductsData: any,
  setGeneralInfoChangesMade: Dispatcher<boolean>,
  allowConcurrentLineCrossingIntruderProducts = false
) => {
  return (
    <Switch
      checked={selectedGroups[groupLabelName][object.label_name]}
      color="warning"
      disabled={disabledChecker(
        object.label_name,
        selectedGroups,
        allowConcurrentLineCrossingIntruderProducts
      )}
      onChange={(event: any) => {
        // update productsData and localProductsData with the new active state
        setLocalProductsData((previousState: any) => {
          //find the index of the product from the previous state whos label_name matches labelName
          const updatedProducts = [...previousState];
          const productIndex = updatedProducts.findIndex(
            (product: any) => product.label_name === groupLabelName
          );
          const updatedProduct = updatedProducts[productIndex];
          const metric = updatedProduct.metrics[object.label_name];
          metric.active = event.target.checked;
          if (
            event.target.checked &&
            shouldApplyAutopatrolHighSensitivityDefault()
          ) {
            applyDefaultHighSensitivityIfUnset(metric);
          }
          updatedProducts[productIndex] = updatedProduct;
          return updatedProducts;
        });
        setProductsData((previousState: any) => {
          const updatedProducts = [...previousState];
          const productIndex = updatedProducts.findIndex(
            (product: any) => product.label_name === groupLabelName
          );
          const updatedProduct = updatedProducts[productIndex];
          const metric = updatedProduct.metrics[object.label_name];
          metric.active = event.target.checked;
          if (
            event.target.checked &&
            shouldApplyAutopatrolHighSensitivityDefault()
          ) {
            applyDefaultHighSensitivityIfUnset(metric);
          }
          updatedProducts[productIndex] = updatedProduct;

          return updatedProducts;
        });
        setGeneralInfoChangesMade(true);
        if (event.target.checked) {
          setSelectedGroups((previousState: any) => {
            const updatedState = { ...previousState };
            const updatedMetric = updatedState[groupLabelName];
            updatedMetric[object.label_name] = event.target.checked;
            updatedMetric.selected = event.target.checked;
            if (groupLabelName === "intruder") {
              if (
                !updatedState.intruderPlus.intruder_plus &&
                !updatedState.intruderPlus.intruder_plus &&
                !updatedState.intruderPlus.vehicle_plus
              ) {
                updatedState.intruderPlus.disabled = true;
              }
            }
            if (groupLabelName === "intruderPlus") {
              if (
                !updatedState.intruder.bike &&
                !updatedState.intruder.vehicle &&
                !updatedState.intruder.intruder
              ) {
                updatedState.intruder.disabled = true;
              }
            }
            if (
              groupLabelName === "motionPlus" ||
              (!allowConcurrentLineCrossingIntruderProducts &&
                groupLabelName === "lineCrossing")
            ) {
              updatedState.intruder.disabled = true;
            }
            updatedState[groupLabelName] = updatedMetric;
            return updatedState;
          });
        }
        if (!event.target.checked) {
          setSelectedGroups((previousState: any) => {
            const updatedState = { ...previousState };
            const updatedMetric = updatedState[groupLabelName];
            let anyMetricTrue = false;
            for (const key in updatedMetric) {
              if (
                updatedMetric[key] &&
                key !== object.label_name &&
                key !== "selected" &&
                key !== "disabled"
              ) {
                anyMetricTrue = true;
              }
            }
            if (!anyMetricTrue) {
              updatedMetric.selected = false;
            }
            if (groupLabelName === "intruder" && !anyMetricTrue) {
              updatedState.intruderPlus.disabled = false;
            }
            if (groupLabelName === "intruderPlus" && !anyMetricTrue) {
              updatedState.intruder.disabled = false;
            }
            if (
              groupLabelName === "motionPlus" &&
              !anyMetricTrue &&
              !updatedState.intruderPlus.selected &&
              (!allowConcurrentLineCrossingIntruderProducts
                ? !updatedState.lineCrossing.selected
                : true)
            ) {
              updatedState.intruder.disabled = false;
            }
            if (
              !allowConcurrentLineCrossingIntruderProducts &&
              groupLabelName === "lineCrossing" &&
              !anyMetricTrue &&
              !updatedState.intruderPlus.selected &&
              !updatedState.motionPlus.selected
            ) {
              updatedState.intruder.disabled = false;
            }
            updatedMetric[object.label_name] = event.target.checked;
            updatedState[groupLabelName] = updatedMetric;
            return updatedState;
          });
        }

        // if (event.target.checked) {

        // }
        // if (event.target.checked) {
        //   if (
        //     object.label_name !== "vehicle_plus" &&
        //     object.label_name !== "bike_plus"
        //   ) {
        //     setProductsData((previousState: any) => {
        //       const updatedProducts = [...previousState];
        //       updatedProducts[productIndex].active = true;
        //       return updatedProducts;
        //     });
        //     setLocalProductsData((previousState: any) => {
        //       const updatedProducts = [...previousState];
        //       updatedProducts[productIndex].active = true;
        //       return updatedProducts;
        //     });
        //     switchDisabler(
        //       productsData[productIndex].metrics[productMetricsIndex]
        //         .display_name,
        //       event.target.checked,
        //       intruderDisabled,
        //       intruderPlusDisabled,
        //       setIntruderDisabled,
        //       setIntruderPlusDisabled,
        //       disabledProducts,
        //       setDisabledProducts
        //     );
        //   } else {
        //     setProductsData((previousState: any) => {
        //       const updatedProducts = [...previousState];
        //       updatedProducts[1].active = true;
        //       return updatedProducts;
        //     });
        //     setLocalProductsData((previousState: any) => {
        //       const updatedProducts = [...previousState];
        //       updatedProducts[1].active = true;
        //       return updatedProducts;
        //     });
        //     switchDisabler(
        //       productsData[1].metrics[productMetricsIndex].display_name,
        //       event.target.checked,
        //       intruderDisabled,
        //       intruderPlusDisabled,
        //       setIntruderDisabled,
        //       setIntruderPlusDisabled,
        //       disabledProducts,
        //       setDisabledProducts
        //     );
        //   }
        // }
        // if (!event.target.checked) {
        //   let anyMetricTrue = false;
        //   productsData[productIndex].metrics.forEach((metric: any) => {
        //     if (
        //       metric.active &&
        //       metric.label_name !==
        //         productsData[productIndex].metrics[productMetricsIndex]
        //           .label_name
        //     ) {
        //       anyMetricTrue = true;
        //     }
        //   });
        //   if (!anyMetricTrue) {
        //     setProductsData((previousState: any) => {
        //       const updatedProducts = [...previousState];
        //       updatedProducts[productIndex].active = false;
        //       return updatedProducts;
        //     });
        //     setLocalProductsData((previousState: any) => {
        //       const updatedProducts = [...previousState];
        //       updatedProducts[productIndex].active = false;
        //       return updatedProducts;
        //     });
        //   }
        //   switchDisabler(
        //     productsData[productIndex].metrics[productMetricsIndex]
        //       .display_name,
        //     event.target.checked,
        //     intruderDisabled,
        //     intruderPlusDisabled,
        //     setIntruderDisabled,
        //     setIntruderPlusDisabled,
        //     disabledProducts,
        //     setDisabledProducts
        //   );
        // }
        // if (
        //   object.label_name !== "vehicle_plus" &&
        //   object.label_name !== "bike_plus"
        // ) {
        //   setPatchObject((previousState: any) => {
        //     return {
        //       ...previousState,
        //       streams: [
        //         handlePatchActiveProducts(event.target.checked, metricIndex),
        //       ],
        //     };
        //   });
        //   setLocalProductsData((previousState: any) => {
        //     const updatedProducts = [...previousState];
        //     updatedProducts[productIndex].metrics[productMetricsIndex].active =
        //       event.target.checked;
        //     return updatedProducts;
        //   });
        //   setProductsData((previousState: any) => {
        //     const updatedProducts = [...previousState];
        //     updatedProducts[productIndex].metrics[productMetricsIndex].active =
        //       event.target.checked;
        //     return updatedProducts;
        //   });
        // } else if (object.label_name === "vehicle_plus") {
        //   console.log("selectedProducts", selectedProducts);
        //   setPatchObject((previousState: any) => {
        //     return {
        //       ...previousState,
        //       streams: [
        //         handlePatchActiveProducts(event.target.checked, metricIndex),
        //       ],
        //     };
        //   });
        //   if (!selectedProducts.intruder_plus && !selectedProducts.bike_plus) {
        //     setLocalProductsData((previousState: any) => {
        //       const updatedProducts = [...previousState];
        //       updatedProducts[1].active = event.target.checked;
        //       return updatedProducts;
        //     });
        //     setProductsData((previousState: any) => {
        //       const updatedProducts = [...previousState];
        //       updatedProducts[1].active = event.target.checked;
        //       return updatedProducts;
        //     });
        //   }
        // } else if (object.label_name === "bike_plus") {
        //   setPatchObject((previousState: any) => {
        //     return {
        //       ...previousState,
        //       streams: [
        //         handlePatchActiveProducts(event.target.checked, metricIndex),
        //       ],
        //     };
        //   });
        //   if (
        //     !selectedProducts.intruder_plus &&
        //     !selectedProducts.vehicle_plus
        //   ) {
        //     setLocalProductsData((previousState: any) => {
        //       const updatedProducts = [...previousState];
        //       updatedProducts[1].active = event.target.checked;
        //       return updatedProducts;
        //     });
        //     setProductsData((previousState: any) => {
        //       const updatedProducts = [...previousState];
        //       updatedProducts[1].active = event.target.checked;
        //       return updatedProducts;
        //     });
        //   }
        // }

        // setGeneralInfoChangesMade(true);
      }}
    />
  );
};
